/**
 * /app/$tenantSlug/people/$id — employee detail view (CC2 2026-06-05).
 *
 * Fetches the employee record by ID from the HR-only directory
 * (GET /api/v1/employees). Missing / out-of-scope / malformed IDs are
 * handled GRACEFULLY in-component (B-A5 2026-06-27): a friendly
 * "Employee not found" Alert is rendered instead of throwing into
 * TanStack Router's `defaultErrorComponent` (RouteError) — deep-linking
 * /people/999999 must never leak a stack trace + source paths to the
 * end user. This mirrors the not-found pattern in
 * app.$tenantSlug.leave.$id.tsx.
 *
 * HR admins see the full record from the HR directory. A non-HR caller
 * gets one of two outcomes (B-A7 2026-06-28): if the requested id is
 * their OWN user/employee id, their self-service profile is rendered
 * (GET /me/profile via useMyProfile) so they aren't left staring at a
 * blank shell; otherwise an explicit "You don't have access to this
 * profile" card — distinct from the HR-path "Employee not found" Alert,
 * which is reserved for genuinely-missing ids when the caller IS HR.
 *
 * URL: /app/{slug}/people/{id}  (parent of /{id}/edit)
 */

import { useState, type ReactNode } from "react";
import { useDetailsStatus } from "@/hooks/use-detail-requests";
import { AskForDetailsDialog } from "@/components/details/ask-for-details-dialog";
import { DetailsOnRecordCard, WaitingOnCard } from "@/components/details/employee-details-cards";
import { z } from "zod";
import { createFileRoute, Link, Navigate, Outlet, useRouterState, useParams } from "@tanstack/react-router";
import { ArrowLeft, Lock, User } from "lucide-react";
import { useAuth, usePoshOnly } from "@/lib/auth";
import { ownPayLocked, OWN_PAY_NOTE } from "@/lib/own-pay";
import type { Role } from "@/types/auth";
import type { Employee } from "@/types/hr";
import { useAssetTypes, useAssets, useEmployeeAccountStatus, useEmployees } from "@/hooks/use-hr";
import { startOffboardingBlocker } from "@/lib/offboarding-rules";
import {
  useLeaveBalance,
  useLeaveRequestsForUser,
  useLeaveTypes,
} from "@/hooks/use-leave";
import { fmtDays, halfDayLabel } from "@/lib/leave-format";
import { visibleBalances } from "@/lib/leave-balance-view";
import type { LeaveType } from "@/types/leave";
import { formatPaise } from "@/lib/expense-format";
import { formatDate } from "@/lib/format";
import { isFutureJoiner, joiningDateOf } from "@/lib/joining";
import { joinsOnLabel } from "@/lib/hiring-recruiter";
import { istTodayISO } from "@/lib/tax-return-periods";
import { meProfileKeys } from "@/hooks/use-me-profile";
import { getMyProfile } from "@/lib/api/me-profile";
import { directoryKeys, useDirectory } from "@/hooks/use-directory";
import { directoryApi, toApiError } from "@/lib/api";
import { DottedLinesCard } from "@/components/org-chart/dotted-lines-card";
import { EmployeePayrollSettings } from "@/components/payroll/employee-payroll-settings";
import { SalaryHistory } from "@/components/payroll/salary-history";
import { PreviousEmploymentCard } from "@/components/payroll/previous-employment";
import { OpeningYTDCard } from "@/components/payroll/opening-ytd";
import { InviteStatusCard, SignedInPasswordReset } from "@/components/people/invite-status-card";
import { DeleteMistakenDialog } from "@/components/people/delete-mistaken-dialog";
import { Reset2FADialog } from "@/components/people/reset-2fa-dialog";
import { canOfferReset2FA } from "@/lib/two-factor-security";
import { ProbationCard } from "@/components/people/probation-card";
import { AppointmentLetterCard } from "@/components/people/appointment-letter-card";
import { StatutoryFormsCard } from "@/components/people/statutory-forms-card";
import { OnboardingStatusCard } from "@/components/people/onboarding-status-card";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEmployeeSalary } from "@/hooks/use-payroll";
import { rupees as salaryRupees, yearlyCTCNote } from "@/lib/salary-calc";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const CAN_VIEW_EMPLOYEES = new Set<Role>(["company_admin", "hr_manager"]);

// The open HR tab lives in the URL (?tab=pay) so a reload, a back/forward
// or a shared link lands on the same tab (2026-10 browser QA payroll #22 —
// a reload of the Pay tab used to drop HR back on Overview).
const EMPLOYEE_TABS = ["overview", "leave", "assets", "pay"] as const;
type EmployeeTab = (typeof EMPLOYEE_TABS)[number];
const employeeDetailSearchSchema = z.object({
  tab: z.enum(EMPLOYEE_TABS).optional().catch(undefined),
  // ?appointment=1 opens the appointment-letter dialog (the "Add
  // employee" success path links here — wave B).
  appointment: z.coerce.number().optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/people/$id",
)({
  validateSearch: employeeDetailSearchSchema,
  // A9 hover-prefetch. The HR-only /employees read is role-gated
  // (guaranteed 403 for a non-HR caller) so it stays component-owned;
  // instead warm the two reads this page issues for any caller: the
  // include-inactive directory (name/manager/dotted-line resolution, also
  // shared by the People list) and the self-profile (rendered when the
  // caller opens their own id). Both are auth-only endpoints.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: meProfileKeys.all,
      queryFn: ({ signal }) => getMyProfile(signal),
    });
    return context.queryClient
      .ensureQueryData({
        queryKey: directoryKeys.list(true),
        queryFn: ({ signal }) =>
          directoryApi.listDirectory({ includeInactive: true }, signal),
      })
      .catch(() => undefined);
  },
  component: PeopleDetailRoute,
});

// ---------------------------------------------------------------------------
// Self-profile rendering (B-A7). When a non-HR caller opens their OWN id we
// show their self-service profile from GET /me/profile. These helpers mirror
// the section layout of app.$tenantSlug.me.profile.tsx — kept compact + local
// (no cross-file export) so this slice stays self-contained.
// ---------------------------------------------------------------------------

const EMPTY = "—";

function fmtText(v?: string): string {
  const t = v?.trim();
  return t ? t : EMPTY;
}

/** "5 Nov 2026" (lib/format) — was the browser locale's "November 5, 2026".
 *  Handles both a timestamp (the HR /employees hire_date) and a plain
 *  calendar date without a timezone shift. */
function fmtDate(v?: string): string {
  return formatDate(v, EMPTY);
}

function fmtEnum(v?: string): string {
  const t = v?.trim();
  if (!t) return EMPTY;
  const spaced = t.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function PeopleDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const { id } = Route.useParams();
  const { tab, appointment } = Route.useSearch();
  const navigate = Route.useNavigate();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const { user } = useAuth();
  const allowed = !!user?.role && CAN_VIEW_EMPLOYEES.has(user.role);
  // POSH only (migration 162): the people list without the HR platform —
  // no leave / assets / pay tabs, probation, letters, statutory forms or
  // exit. Those reads would answer 403 upgrade_required (→ Billing).
  const poshOnly = usePoshOnly();
  const platform = allowed && !poshOnly;
  // B-A7: a non-HR caller who deep-links their OWN id (employees.id ===
  // the active membership's user_id) should see their self-service
  // profile rather than a blank shell; any OTHER id is a flat no-access.
  const isOwnProfile =
    !allowed && validId && !!user && user.user_id === numId;
  // When a child route (e.g. /edit) is active, delegate rendering to it
  // via <Outlet /> instead of showing the detail card. This allows
  // people.$id.edit.tsx to render its full EditEmployeeForm without
  // being overlaid by the detail view.
  const routerState = useRouterState();
  const hasActiveChild = routerState.location.pathname.endsWith("/edit");

  const q = useEmployees(allowed && validId);
  // QA 2026-10 #18 — the account status says when the company-admin rules
  // would refuse starting this person's exit; the action is then not offered.
  const accountStatus = useEmployeeAccountStatus(numId, allowed && validId);
  const startOffboardingRefused = !!startOffboardingBlocker(accountStatus.data?.deactivation_blockers);
  // The HR /employees DTO doesn't carry matrix lines; the shared /directory DTO
  // does. Fetch it (non-fatal) to surface this person's dotted reporting lines
  // (docs/50 F3). The directory is auth-only for ANY tenant user (not the HR-only
  // /employees), so it ALSO powers the W2-07 manager-of-a-direct-report view: a
  // non-HR caller needs it to (a) decide whether the target reports to them and
  // (b) render the directory-grade card. Fetch whenever the id is valid and the
  // caller isn't viewing their own self-service profile.
  const dir = useDirectory({
    includeInactive: true,
    enabled: validId && !isOwnProfile,
  });
  const dirEntry = dir.byId.get(numId);

  // W2-07: a non-HR caller who is the target's DIRECT manager may see a
  // directory-grade, read-only card (no PII) — the directory already invites the
  // click (rows + the "Reports to" line link here). "Is my report" is decided
  // purely from non-PII directory data: the target's entry.manager_id === me.
  const isMyReport =
    !allowed &&
    !isOwnProfile &&
    validId &&
    !!user &&
    dirEntry?.manager_id === user.user_id;

  // Once data is loaded, find the employee in the role-scoped list.
  const employee = q.data?.employees.find((e) => e.id === numId);

  // Ask for Details (HR, an active person who is not you; the server
  // refuses your own record and a person who has left).
  const askable =
    platform && !!employee && employee.is_active && employee.id !== user?.user_id;
  const detailsStatus = useDetailsStatus(numId, askable);
  const [ask, setAsk] = useState<{ open: boolean; addTo?: number }>({ open: false });
  const openAddMore = (requestId: number) => setAsk({ open: true, addTo: requestId });

  // Resolve graceful UI states instead of throwing into the raw error
  // boundary (B-A5). Mirrors leave.$id.tsx: a hard fetch/authz error gets a
  // destructive Alert; a missing/out-of-scope/malformed id gets a friendly
  // "not found" Alert — never a stack trace.
  // A restored (persisted) directory may predate this employee: while the
  // revalidating fetch is in flight, "not in the list" counts as loading.
  const isLoading =
    validId && allowed && (q.isLoading || (!employee && q.isFetching));
  // "Not found" is reserved for the HR path: an invalid id, or a record
  // that simply isn't in the HR-scoped list. A non-HR caller is NOT routed
  // here — they get either their own profile (isOwnProfile) or an explicit
  // no-access card (below), never the "Employee not found" alert.
  const genuinelyNotFound =
    (allowed && !validId) ||
    (allowed && !q.isError && !!q.data && !employee && !q.isFetching);
  // A non-HR, non-self caller needs the directory to decide between the
  // manager-of-a-direct-report card (W2-07) and the flat no-access lock. While
  // the directory is still loading we show a skeleton instead of flashing the
  // lock (the target may turn out to be their report).
  const nonHrLookup = !allowed && !isOwnProfile;
  const dirResolving = nonHrLookup && validId && dir.isLoading;
  // A non-HR caller looking at someone else's id who is NOT their direct report
  // (or an invalid id, or the directory failed/finished without the target): a
  // flat, honest "no access" — distinct from the HR not-found alert, the
  // directory-grade manager card, and the B-A5 graceful-degradation path.
  const noAccess =
    nonHrLookup && (!validId || (!dirResolving && !isMyReport));

  // Child route active (e.g. /edit) — render its content via Outlet.
  if (hasActiveChild) {
    return <Outlet />;
  }

  // B-A7: someone who opens their OWN id here (e.g. "Reports to <you>" on
  // My Team) gets Me → Profile, the page that is theirs.
  if (isOwnProfile) {
    return <Navigate to="/app/$tenantSlug/me/profile" params={{ tenantSlug }} replace />;
  }

  return (
    <>
      <PageHeader
        title={
          employee
            ? `${employee.first_name} ${employee.last_name}`
            : isMyReport && dirEntry
              ? dirEntry.display_name ||
                `${dirEntry.first_name} ${dirEntry.last_name}`.trim()
              : "Employee"
        }
        description={
          employee?.designation ??
          (isMyReport ? dirEntry?.designation : undefined) ??
          undefined
        }
        actions={
          askable ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              data-testid="ask-for-details-button"
              onClick={() =>
                detailsStatus.data?.open_request
                  ? openAddMore(detailsStatus.data.open_request.id)
                  : setAsk({ open: true })
              }
            >
              Ask for Details
            </Button>
          ) : undefined
        }
      />

      {askable && employee && (
        <AskForDetailsDialog
          open={ask.open}
          onOpenChange={(o) => setAsk((p) => ({ ...p, open: o }))}
          userId={employee.id}
          firstName={employee.first_name}
          status={detailsStatus.data}
          addToRequestId={ask.addTo}
        />
      )}

      {/* Phones only: the desktop breadcrumb (People › name) goes back. */}
      <div className="mb-4 lg:hidden">
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back to People
          </Link>
        </Button>
      </div>

      {isLoading && (
        <Card className="max-w-2xl">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </CardContent>
        </Card>
      )}

      {q.isError && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertTitle>Could not load this employee</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {genuinelyNotFound && (
        <Alert className="max-w-2xl">
          <AlertTitle>Employee not found</AlertTitle>
          <AlertDescription>
            This employee doesn&apos;t exist or isn&apos;t visible to your
            role.{" "}
            <Link
              to="/app/$tenantSlug/people"
              params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to People
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      {dirResolving && (
        <Card className="max-w-2xl">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </CardContent>
        </Card>
      )}

      {noAccess && (
        <Card className="max-w-2xl">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Lock aria-hidden className="h-6 w-6" />
            </span>
            <div>
              <p className="font-medium">You don&apos;t have access to this profile</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Only HR can view other employees&apos; records. You can always{" "}
                <Link
                  to="/app/$tenantSlug/me/profile"
                  params={{ tenantSlug }}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  view your own profile
                </Link>
                .
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/*
        W2-07: directory-grade read-only card for a non-HR manager viewing their
        DIRECT report. Renders ONLY non-PII fields the manager already sees in the
        directory (name / designation / department / reporting line) — never
        salary / bank / PAN / address / DOB / email or any HR-only PII. There is
        no Edit action (HR-only) and no full HR detail card here.
      */}
      {isMyReport && dirEntry && (
        <Card className="max-w-2xl">
          <CardHeader className="flex flex-row items-center gap-3 pb-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User aria-hidden className="h-6 w-6" />
            </span>
            <div>
              <CardTitle className="text-lg">
                {dirEntry.display_name ||
                  `${dirEntry.first_name} ${dirEntry.last_name}`.trim()}
              </CardTitle>
              {dirEntry.designation && (
                <p className="text-sm text-muted-foreground">
                  {dirEntry.designation}
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm [&>*]:min-w-0 [&_dd]:[overflow-wrap:anywhere]">
              <div>
                <dt className="font-medium text-muted-foreground">
                  Designation
                </dt>
                <dd className="mt-0.5">{fmtText(dirEntry.designation)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Department
                </dt>
                <dd className="mt-0.5">{fmtText(dirEntry.department)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Reports To
                </dt>
                <dd className="mt-0.5">{dir.label(dirEntry.manager_id)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Status</dt>
                <dd className="mt-0.5">
                  <StatusBadge
                    status={dirEntry.is_active ? "active" : "inactive"}
                  />
                </dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground">
              You&apos;re seeing the directory profile for someone who reports to
              you. Personal and HR details are visible to HR only.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Name + designation already live in the PageHeader above — no
          duplicated card header here.

          HR "employee 360" tabs (2026-08-07): Overview keeps the original
          detail card + quick links; Leave / Assets / Pay answer "tell me
          about this employee" without hopping modules. This tabbed view
          only ever renders on the HR path (`employee` comes from the
          HR-only /employees list), so the Pay tab — like the salary
          fields on the edit form — is visible to company_admin /
          hr_manager exclusively; the self-profile and manager
          (directory-grade) variants above are untouched. Radix keeps
          inactive TabsContent unmounted, so each tab's queries fire only
          when it is first opened. */}
      {employee && (
        <Tabs
          value={poshOnly ? "overview" : (tab ?? "overview")}
          onValueChange={(v) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                tab: v === "overview" ? undefined : (v as EmployeeTab),
              }),
              replace: true,
            })
          }
          className="max-w-2xl"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {!poshOnly && (
              <>
                <TabsTrigger value="leave">Leave</TabsTrigger>
                <TabsTrigger value="assets">Assets</TabsTrigger>
                <TabsTrigger value="pay">Pay</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            {askable && detailsStatus.data?.open_request && (
              <WaitingOnCard
                requestId={detailsStatus.data.open_request.id}
                firstName={employee.first_name}
                onAddMore={openAddMore}
              />
            )}
            {askable && (
              <DetailsOnRecordCard
                userId={employee.id}
                onAsk={() =>
                  detailsStatus.data?.open_request
                    ? openAddMore(detailsStatus.data.open_request.id)
                    : setAsk({ open: true })
                }
              />
            )}

            <Card>
              <CardContent className="space-y-4 pt-6">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm [&>*]:min-w-0 [&_dd]:[overflow-wrap:anywhere]">
              <div>
                <dt className="font-medium text-muted-foreground">
                  Employee Code
                </dt>
                <dd className="mt-0.5">{employee.employee_id ?? "—"}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Department
                </dt>
                <dd className="mt-0.5">{employee.department ?? "—"}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Email</dt>
                <dd className="mt-0.5">{employee.email}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Phone</dt>
                <dd className="mt-0.5">{fmtText(employee.phone)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Date of Joining
                </dt>
                <dd className="mt-0.5">{fmtDate(employee.hire_date)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Employment Type
                </dt>
                <dd className="mt-0.5">{fmtEnum(employee.employment_type)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Location</dt>
                <dd className="mt-0.5">{fmtText(employee.location)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">
                  Cost Centre
                </dt>
                <dd className="mt-0.5">{fmtText(employee.cost_center)}</dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Manager</dt>
                <dd className="mt-0.5">
                  {employee.manager_id != null
                    ? dir.label(employee.manager_id, null, false)
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-muted-foreground">Status</dt>
                <dd className="mt-0.5">
                  {/* Wave-B area 2: a hire who hasn't started yet reads
                      "Joins on <date>", not Active. */}
                  {joinsOnLabel(employee.is_active, joiningDateOf(employee), istTodayISO()) ? (
                    <StatusBadge
                      tone="info"
                      label={joinsOnLabel(employee.is_active, joiningDateOf(employee), istTodayISO())!}
                    />
                  ) : (
                    <StatusBadge
                      status={employee.is_active ? "active" : "inactive"}
                    />
                  )}
                </dd>
              </div>
            </dl>

            {allowed && <InviteStatusCard employee={employee} />}
            {allowed && (
              <SignedInPasswordReset employee={employee} isSelf={employee.id === user?.user_id} />
            )}

            {platform && <ProbationCard employee={employee} />}

            {/* Wave B: appointment letter (not on one's own profile — the
                server refuses own_record) + Form 11 / Form F. */}
            {platform && employee.id !== user?.user_id && (
              <AppointmentLetterCard employee={employee} autoOpen={appointment === 1} />
            )}
            {platform && (
              <StatutoryFormsCard
                employeeId={employee.id}
                employeeName={`${employee.first_name} ${employee.last_name}`}
              />
            )}

            {allowed && <OnboardingStatusCard employee={employee} />}

            {allowed && (
              <div className="flex flex-wrap gap-2 pt-2">
                <Button asChild variant="default" size="sm">
                  <Link
                    to="/app/$tenantSlug/people/$id/edit"
                    params={{ tenantSlug, id }}
                  >
                    Edit Employee
                  </Link>
                </Button>
                {platform && employee.is_active && !startOffboardingRefused && (
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to="/app/$tenantSlug/hr/offboarding/new"
                      params={{ tenantSlug }}
                      search={{ employee_id: employee.id }}
                    >
                      Start Offboarding
                    </Link>
                  </Button>
                )}
                {/* Documents opens THIS person's document store (QA
                    2026-10 #5 — it used to open the register unfiltered,
                    and a leaver could not be picked there at all). The
                    asset register and org chart are global views. */}
                {platform && (
                  <>
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to="/app/$tenantSlug/hr/documents"
                        params={{ tenantSlug }}
                        search={{ user_id: employee.id }}
                        data-testid="employee-documents-link"
                      >
                        Documents
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to="/app/$tenantSlug/hr/assets"
                        params={{ tenantSlug }}
                      >
                        Asset Register
                      </Link>
                    </Button>
                  </>
                )}
                <Button asChild variant="outline" size="sm">
                  <Link
                    to="/app/$tenantSlug/people/org-chart"
                    params={{ tenantSlug }}
                  >
                    Org Chart
                  </Link>
                </Button>
                {/* Prospect drop-off review 2026-10: a person added by
                    mistake (a test import, a duplicate) can be removed
                    outright when they have no history; the dialog checks
                    first and explains any refusal. Never your own record. */}
                {/* Passkeys §6.6: a company admin resets a member's 2FA
                    (lost phone / key). Never their own record or a
                    privileged role — the server refuses those anyway. */}
                {canOfferReset2FA({
                  viewerRole: user?.role,
                  viewerId: user?.user_id,
                  targetId: employee.id,
                  targetRole: employee.role,
                }) && <Reset2FADialog employee={employee} />}
                {employee.id !== user?.user_id && employee.role !== "company_admin" && (
                  <DeleteMistakenDialog
                    employeeIds={[employee.id]}
                    onDeleted={(ids) => {
                      if (ids.includes(employee.id)) {
                        void navigate({ to: "/app/$tenantSlug/people", params: { tenantSlug }, search: {} });
                      }
                    }}
                  />
                )}
              </div>
            )}
              </CardContent>
            </Card>

            {dirEntry && (
              <DottedLinesCard
                managers={dirEntry.dotted_managers}
                reports={dirEntry.dotted_reports}
              />
            )}
          </TabsContent>

          {!poshOnly && (
          <>
          <TabsContent value="leave" className="mt-4">
            <EmployeeLeaveTab userId={employee.id} gender={employee.gender} />
          </TabsContent>

          <TabsContent value="assets" className="mt-4">
            <EmployeeAssetsTab userId={employee.id} />
          </TabsContent>

          <TabsContent value="pay" className="mt-4">
            <EmployeePayTab employee={employee} tenantSlug={tenantSlug} />
          </TabsContent>
          </>
          )}
        </Tabs>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Employee-360 tab panels (2026-08-07). All three are HR-view-only (they
// render inside the `employee &&` branch, which requires the HR-only
// /employees read to have succeeded). Every fetch fails SOFT: an error
// renders a quiet muted line inside the tab — never a crashed page and
// never a shouty destructive alert for what is auxiliary context.
// ---------------------------------------------------------------------------

function TabSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-2/3" />
    </div>
  );
}

function TabQuiet({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <p className="py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * Leave tab — current leave-year per-type balances + the employee's ~10
 * most recent requests. Balances reuse GET /leave/balance?user_id= (the
 * server allows it for HR or the target's approver chain — same
 * endpoint the admin leave surfaces use) with NO year, so the server
 * resolves the tenant's current leave-year (FY-aware); requests use the
 * role-scoped list with the narrowing-only ?user_id= filter.
 *
 * QA 2026-10 leave: a type the employee has not used yet shows its
 * computed entitlement (#1), Comp-Off shows the comp-off CREDIT left —
 * not "Unlimited" (#2), and a half-day request names its half (#16).
 */
function EmployeeLeaveTab({
  userId,
  gender,
}: Readonly<{ userId: number; gender?: string | null }>) {
  const balQ = useLeaveBalance(userId);
  const yearLabel = balQ.data?.leave_year?.label ?? "";
  const typesQ = useLeaveTypes();
  const reqQ = useLeaveRequestsForUser(userId);

  const leaveTypeById = new Map<number, LeaveType>(
    (typesQ.data?.leave_types ?? []).map((t) => [t.id, t]),
  );
  const typeLabel = (id: number) => {
    const t = leaveTypeById.get(id);
    return t ? `${t.name} (${t.code})` : `Type #${id}`;
  };

  const balances = visibleBalances(
    balQ.data?.balances ?? [],
    leaveTypeById,
    gender,
  );
  const compOff = balQ.data?.comp_off;
  const recent = [...(reqQ.data?.leave_requests ?? [])]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 10);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Leave Balances{yearLabel ? ` — ${yearLabel}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {balQ.isLoading && <TabSkeleton />}
          {balQ.isError && (
            <TabQuiet>Leave balances aren&apos;t available right now.</TabQuiet>
          )}
          {balQ.data && balances.length === 0 && !compOff && (
            <TabQuiet>
              No leave balance is recorded for {yearLabel || "this leave year"}.
            </TabQuiet>
          )}
          {(balances.length > 0 || compOff) && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Granted</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.map((b) => {
                  const unlimited =
                    leaveTypeById.get(b.leave_type_id)?.unlimited ?? false;
                  return (
                    <TableRow key={b.leave_type_id}>
                      <TableCell className="font-medium">
                        {typeLabel(b.leave_type_id)}
                      </TableCell>
                      <TableCell className="text-right">
                        {unlimited ? "Unlimited" : fmtDays(b.granted_days)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtDays(b.used_days)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtDays(b.pending_days)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {unlimited ? "—" : fmtDays(b.available)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {compOff && (
                  <TableRow key="comp-off">
                    <TableCell className="font-medium">
                      {typeLabel(compOff.leave_type_id)}
                      <span className="block text-xs font-normal text-muted-foreground">
                        Comp-off credit
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtDays(compOff.credit_days)}
                    </TableCell>
                    <TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right">
                      {fmtDays(compOff.pending_days)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtDays(compOff.available_days)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {reqQ.isLoading && <TabSkeleton />}
          {reqQ.isError && (
            <TabQuiet>
              Leave requests aren&apos;t available right now.
            </TabQuiet>
          )}
          {reqQ.data && recent.length === 0 && (
            <TabQuiet>No leave requests yet.</TabQuiet>
          )}
          {recent.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dates</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">
                      {fmtDate(r.start_date)}
                      {r.end_date !== r.start_date && (
                        <span className="text-muted-foreground">
                          {" "}
                          – {fmtDate(r.end_date)}
                        </span>
                      )}
                      {r.half_day && (
                        <span className="text-muted-foreground">
                          {" "}
                          · {halfDayLabel(r.half_day)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{typeLabel(r.leave_type_id)}</TableCell>
                    <TableCell className="text-right">
                      {fmtDays(r.total_days)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Assets tab — the employee's current custody, via the existing
 * GET /hr/assets?assigned_to= register filter (docs/60 A2 ledger's
 * live "who holds what" view; purchase value deliberately omitted —
 * it belongs on the register/offboarding surfaces).
 */
function EmployeeAssetsTab({ userId }: Readonly<{ userId: number }>) {
  const assetsQ = useAssets(undefined, userId);
  const typesQ = useAssetTypes();

  const typeName = (id: number) =>
    typesQ.data?.asset_types.find((t) => t.id === id)?.name ?? `Type #${id}`;
  const assets = assetsQ.data?.assets ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Assets in Custody</CardTitle>
      </CardHeader>
      <CardContent>
        {assetsQ.isLoading && <TabSkeleton />}
        {assetsQ.isError && (
          <TabQuiet>Asset data isn&apos;t available right now.</TabQuiet>
        )}
        {assetsQ.data && assets.length === 0 && (
          <TabQuiet>No assets are assigned to this employee.</TabQuiet>
        )}
        {assets.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Tag / Serial</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned Since</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    {typeName(a.asset_type_id)}
                  </TableCell>
                  <TableCell>
                    {[a.asset_tag, a.serial_number]
                      .map((s) => s?.trim())
                      .filter(Boolean)
                      .join(" · ") || EMPTY}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={a.status} />
                  </TableCell>
                  <TableCell>{fmtDate(a.assigned_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function maskAccount(v?: string): string {
  const t = v?.trim();
  if (!t) return EMPTY;
  return t.length <= 4 ? t : `•••• ${t.slice(-4)}`;
}

/**
 * Pay tab — the salary structure already on the HR employee DTO
 * (monthly figures, same fields the edit form's compensation section
 * writes), formatted with the shared integer-paise formatter. Bank
 * account is masked to last-4 here (payslip PII discipline); the full
 * number stays on the edit form. Per-run payslip rows are deliberately
 * NOT aggregated here — no cheap per-employee cross-run endpoint
 * exists — so we link to Payroll instead.
 */
/**
 * People → Pay: the yearly CTC (gross + employer PF + ESI + gratuity) from the
 * person's salary — the offer salary calculator's own lines
 * (GET /payroll/employees/{id}/salary, HR). Quiet on error.
 */
function YearlyCTC({ employeeId }: Readonly<{ employeeId: number }>) {
  const q = useEmployeeSalary(employeeId);
  const b = q.data?.salary ?? null;
  return (
    <div className="col-span-2" data-testid="employee-pay-yearly-ctc">
      <dt className="font-medium text-muted-foreground">Yearly CTC</dt>
      <dd className="mt-0.5">
        {q.isLoading ? (
          <Skeleton className="h-5 w-40" />
        ) : b ? (
          <>
            <span className="font-medium">{salaryRupees(b.ctc_annual_paise)}</span>
            <span className="block text-xs text-muted-foreground">{yearlyCTCNote(b)}</span>
          </>
        ) : (
          EMPTY
        )}
      </dd>
    </div>
  );
}

function EmployeePayTab({
  employee,
  tenantSlug,
}: Readonly<{ employee: Employee; tenantSlug: string }>) {
  const basic = employee.salary_basic_paise;
  const hra = employee.salary_hra_paise;
  const special = employee.salary_special_allowance_paise;
  const hasStructure = basic != null || hra != null || special != null;
  const gross = (basic ?? 0) + (hra ?? 0) + (special ?? 0);
  // Nobody edits their own pay (server: 403 own_record) — no "add it via
  // Edit employee" pointer on the viewer's own Pay tab.
  const { user } = useAuth();
  const own = ownPayLocked(employee.id, user);
  // Before the joining date nothing is paid yet: say when the salary
  // starts, not "today's" salary.
  const joining = joiningDateOf(employee);
  const notJoined = isFutureJoiner(joining, istTodayISO());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Compensation</CardTitle>
        <p className="text-xs text-muted-foreground" data-testid="employee-pay-caption">
          {notJoined
            ? `Salary from ${formatDate(joining)}, the joining date. Changes are recorded with an effective date in the salary history below.`
            : "Current monthly salary. Changes are recorded with an effective date in the salary history below."}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasStructure && own && (
          <TabQuiet>No salary structure on record. {OWN_PAY_NOTE}</TabQuiet>
        )}
        {!hasStructure && !own && (
          <TabQuiet>
            No salary structure on record — add it via{" "}
            <Link
              to="/app/$tenantSlug/people/$id/edit"
              params={{ tenantSlug, id: String(employee.id) }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Edit employee
            </Link>
            .
          </TabQuiet>
        )}

        {hasStructure && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm [&>*]:min-w-0 [&_dd]:[overflow-wrap:anywhere]">
            <div>
              <dt className="font-medium text-muted-foreground">
                Basic Salary (₹/Month)
              </dt>
              <dd className="mt-0.5">
                {basic != null ? formatPaise(basic) : EMPTY}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">
                HRA (₹/Month)
              </dt>
              <dd className="mt-0.5">
                {hra != null ? formatPaise(hra) : EMPTY}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">
                Special Allowance (₹/Month)
              </dt>
              <dd className="mt-0.5">
                {special != null ? formatPaise(special) : EMPTY}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-muted-foreground">
                Gross (₹/Month)
              </dt>
              <dd className="mt-0.5 font-medium">{formatPaise(gross)}</dd>
            </div>
            <YearlyCTC employeeId={employee.id} />
          </dl>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-4 text-sm [&>*]:min-w-0 [&_dd]:[overflow-wrap:anywhere]">
          <div>
            <dt className="font-medium text-muted-foreground">
              PF Applicable
            </dt>
            <dd className="mt-0.5">{employee.pf_applicable ? "Yes" : "No"}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">Tax Regime</dt>
            <dd className="mt-0.5">
              {employee.tax_regime === "old"
                ? "Old regime"
                : "New regime (§115BAC)"}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">PAN</dt>
            <dd className="mt-0.5">{fmtText(employee.pan)}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">PF UAN</dt>
            <dd className="mt-0.5" data-testid="employee-pay-pf-uan">
              {fmtText(employee.pf_uan ?? undefined)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">
              ESI IP Number
            </dt>
            <dd className="mt-0.5" data-testid="employee-pay-esi-number">
              {fmtText(employee.esi_number ?? undefined)}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">
              Bank Account
            </dt>
            <dd className="mt-0.5">
              {maskAccount(employee.bank_account_number)}
              {employee.bank_ifsc?.trim() ? ` · ${employee.bank_ifsc}` : ""}
            </dd>
          </div>
        </dl>

        {/* Wave 2 — not-on-payroll toggle + the employee's own PT state. */}
        <EmployeePayrollSettings employeeId={employee.id} />

        {/* Wave 2 (migration 113) — effective-dated salary history, revisions
            and arrears for locked months (correction runs). */}
        <SalaryHistory employeeId={employee.id} tenantSlug={tenantSlug} joiningDate={joining} />

        {/* Wave 2 (migration 114) — previous employer this year (Form 12B),
            feeds the automatic TDS of a mid-year joiner. */}
        <PreviousEmploymentCard employeeId={employee.id} />

        {/* Migration 128 (audit F8) — salary + TDS this employer paid before
            payroll moved to WorkRight this year (counts in the TDS
            year-to-date, Form 16 and the 24Q). */}
        <OpeningYTDCard employeeId={employee.id} />

        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
              Open Payroll
            </Link>
          </Button>
          <p className="text-xs text-muted-foreground">
            Monthly earnings and payslips live on the locked payroll runs.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
